import { ClientMsgKind } from '@infiltre/shared';
import { selectRoom, useCanOpenVote } from '@/entities/room';
import { SpeakingOrder } from '@/features/game-round';
import { formatCountdown, useCountdown } from '@/shared/hooks/use-countdown';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { SoundCue, useSound } from '@/shared/lib/audio';
import { useT } from '@/shared/lib/i18n';
import { CountdownChip } from '@/shared/ui';

/**
 * Online discussion phase. Every player sees the same speaking ring + emoji
 * reactions; only the host gets the `OpenVote` CTA — non-hosts see a
 * "waiting for host" line via `SpeakingOrder.canReadyToVote=false`.
 *
 * When `room.discussDeadlineAt` is set (server-side discussion timer active),
 * a `CountdownChip` renders for everyone. Pure display: the server fires the
 * auto-`OpenVote` when the deadline elapses; the orchestrator unmounts on the
 * resulting phase change.
 */
export const OnlineDiscussOrchestrator = () => {
  const t = useT();
  const playSound = useSound();
  const room = useGameStore(selectRoom);
  const driver = useSessionStore((s) => s.driver);
  const identity = useSessionStore((s) => s.identity);
  const canOpenVote = useCanOpenVote();
  const remaining = useCountdown(room?.discussDeadlineAt);

  if (!room || !driver || !identity) return null;
  const remainingSeconds = remaining === null ? null : Math.max(0, Math.floor(remaining / 1000));
  const timerLabel =
    remaining === null
      ? ''
      : remaining <= 0
        ? t('online.discuss.timer_expired')
        : formatCountdown(remaining);

  const timerChip = timerLabel && (
    <div className="flex justify-center pt-2">
      <CountdownChip
        label={timerLabel}
        eyebrow={t('online.discuss.timer_eyebrow')}
        remainingSeconds={remainingSeconds}
      />
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      {timerChip}
      <SpeakingOrder
        players={room.players}
        canReadyToVote={canOpenVote}
        onReadyToVote={() => {
          if (!canOpenVote) return;
          playSound(SoundCue.OpenVote);
          driver.send({ kind: ClientMsgKind.OpenVote, actorPlayerId: identity.playerId });
        }}
      />
    </div>
  );
};
